import { useRef, useCallback, useEffect } from "react";
import "./monaco-setup"; // self-host Monaco (no CDN dependency)
import Editor, { type OnMount, type EditorProps } from "@monaco-editor/react";
import { cn } from "../../../lib/utils";
import { useTheme } from "../../../hooks/use-theme";
import { shouldScrollToRevealLine } from "./prompt-editor-reveal";

export interface PromptEditorProps {
  /** Current content value */
  value: string;
  /** Called when content changes */
  onChange?: (value: string) => void;
  /** Monaco language mode */
  language?: string;
  /** Make editor read-only */
  readOnly?: boolean;
  /** Editor height (CSS value) */
  height?: string | number;
  /** Additional class names for the container */
  className?: string;
  /** Variable names available for {{var}} completion */
  variables?: string[];
  /** 1-based line to scroll into view. */
  revealLine?: number | null;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type MonacoInstance = Parameters<OnMount>[1];
type EditorInstance = Parameters<OnMount>[0];

function scrollEditorToLine(editor: EditorInstance | null, line: number | null | undefined) {
  if (!editor || line == null || line < 1) return;
  editor.revealLineInCenter(line);
  editor.setPosition({ lineNumber: line, column: 1 });
}

export function PromptEditor({
  value,
  onChange,
  language = "markdown",
  readOnly = false,
  height = "400px",
  className,
  variables,
  revealLine,
}: PromptEditorProps) {
  const editorRef = useRef<EditorInstance | null>(null);
  const revealLineRef = useRef(revealLine);
  const revealedLineRef = useRef<number | null>(null);
  revealLineRef.current = revealLine;
  const monacoRef = useRef<MonacoInstance | null>(null);
  const completionDisposable = useRef<{ dispose(): void } | null>(null);
  const { theme } = useTheme();

  const monacoTheme = theme === "dark" ? "vs-dark" : "vs";

  const handleMount: OnMount = useCallback((editorInstance, monacoInstance) => {
    editorRef.current = editorInstance;
    monacoRef.current = monacoInstance;
    editorInstance.focus();
    scrollEditorToLine(editorInstance, revealLineRef.current);

    // Re-measure after custom font loads so cursor position matches glyphs
    document.fonts.ready.then(() => monacoInstance.editor.remeasureFonts());
  }, []);

  useEffect(() => {
    const editor = editorRef.current;
    const lineCount = editor?.getModel()?.getLineCount() ?? 0;
    if (!shouldScrollToRevealLine(lineCount, revealLine, revealedLineRef.current)) {
      if (revealLine == null) revealedLineRef.current = null;
      return;
    }
    scrollEditorToLine(editor, revealLine);
    revealedLineRef.current = revealLine ?? null;
  }, [revealLine, value]);

  // Register / update completion provider whenever variables change
  useEffect(() => {
    const monaco = monacoRef.current;
    if (!monaco || !variables || variables.length === 0) return;

    // Dispose previous provider
    completionDisposable.current?.dispose();

    completionDisposable.current = monaco.languages.registerCompletionItemProvider(language, {
      triggerCharacters: ["{"],
      provideCompletionItems(model: { getLineContent(n: number): string; getWordUntilPosition(p: { lineNumber: number; column: number }): { startColumn: number; endColumn: number } }, position: { lineNumber: number; column: number }) {
        const lineContent = model.getLineContent(position.lineNumber);
        const textBeforeCursor = lineContent.substring(0, position.column - 1);

        // Only suggest when user has typed `{{`
        if (!textBeforeCursor.endsWith("{{")) return { suggestions: [] };

        const word = model.getWordUntilPosition(position);
        const range = {
          startLineNumber: position.lineNumber,
          endLineNumber: position.lineNumber,
          startColumn: word.startColumn,
          endColumn: word.endColumn,
        };

        // Check if `}}` already follows the cursor
        const textAfterCursor = lineContent.substring(position.column - 1);
        const closingExists = textAfterCursor.startsWith("}}");

        const suggestions = variables.map((name) => ({
          label: name,
          kind: monaco.languages.CompletionItemKind.Variable,
          insertText: closingExists ? name : `${name}}}`,
          range,
          detail: "Prompt variable",
        }));

        return { suggestions };
      },
    });

    return () => {
      completionDisposable.current?.dispose();
      completionDisposable.current = null;
    };
  }, [variables, language]);

  const handleChange = useCallback(
    (val: string | undefined) => {
      onChange?.(val ?? "");
    },
    [onChange],
  );

  return (
    <div
      className={cn(
        "overflow-hidden rounded-md border border-[var(--border)]",
        readOnly && "opacity-75",
        className,
      )}
    >
      <Editor
        height={height}
        language={language}
        theme={monacoTheme}
        value={value}
        onChange={handleChange}
        onMount={handleMount}
        options={{
          readOnly,
          minimap: { enabled: false },
          lineNumbers: "on",
          scrollBeyondLastLine: false,
          wordWrap: "on",
          fontSize: 13,
          fontFamily: "'Fragment Mono', monospace",
          padding: { top: 12, bottom: 12 },
          renderLineHighlight: "none",
          overviewRulerLanes: 0,
          hideCursorInOverviewRuler: true,
          scrollbar: {
            vertical: "auto",
            horizontal: "hidden",
          },
        }}
      />
    </div>
  );
}
